import { defineTable } from "convex/server";
import { v, type Infer } from "convex/values";

export const APPOINTMENT_TYPES = {
  CVL: "cvl",
  MVL: "mvl",
  SIMPLIFIED_LIQUIDATION: "simplified_liquidation",
  COURT_LIQUIDATION: "court_liquidation",
  PROVISIONAL_LIQUIDATION: "provisional_liquidation",
  SBR: "sbr",
  VOLUNTARY_ADMINISTRATION: "voluntary_administration",
  DEED_OF_COMPANY_ARRANGEMENT: "deed_of_company_arrangement",
  RECEIVERSHIP: "receivership",
  SCHEME_OF_ARRANGEMENT: "scheme_of_arrangement",
  PERSONAL_BANKRUPTCY: "personal_bankruptcy",
  BUSINESS_BANKRUPTCY: "business_bankruptcy",
  // MOR-1838 — personal insolvency appointments named by how they arose.
  // `personal_bankruptcy` / `business_bankruptcy` stay valid for existing
  // jobs; see `convex/lib/appointmentTypes.ts` for the classification.
  CREDITORS_PETITION: "creditors_petition",
  DEBTORS_PETITION: "debtors_petition",
  PART_X_ARRANGEMENT: "part_x_arrangement",
} as const;

export const appointmentTypeValidator = v.union(
  v.literal("cvl"),
  v.literal("mvl"),
  v.literal("simplified_liquidation"),
  v.literal("court_liquidation"),
  v.literal("provisional_liquidation"),
  v.literal("sbr"),
  v.literal("voluntary_administration"),
  v.literal("deed_of_company_arrangement"),
  v.literal("receivership"),
  v.literal("scheme_of_arrangement"),
  v.literal("personal_bankruptcy"),
  v.literal("business_bankruptcy"),
  v.literal("creditors_petition"),
  v.literal("debtors_petition"),
  v.literal("part_x_arrangement"),
);

export type AppointmentType = Infer<typeof appointmentTypeValidator>;

export const JOB_STATUSES = {
  PRE_APPOINTMENT: "pre_appointment",
  DAY_ONE: "day_one",
  ACTIVE: "active",
  ON_HOLD: "on_hold",
  ARCHIVED: "archived",
  COMPLETED: "completed",
  /**
   * Terminal status for pre-appointment jobs that did not proceed to a
   * formal appointment. Used for reporting on lost pitches / engagements.
   * Only valid as a transition FROM `pre_appointment`.
   */
  DID_NOT_PROCEED: "did_not_proceed",
} as const;

export const jobStatusValidator = v.union(
  v.literal("pre_appointment"),
  v.literal("day_one"),
  v.literal("active"),
  v.literal("on_hold"),
  v.literal("archived"),
  v.literal("completed"),
  v.literal("did_not_proceed"),
);

export type JobStatus = Infer<typeof jobStatusValidator>;

/**
 * Statuses that count as "appointed" — i.e. the firm has been formally
 * appointed and the job has an appointment instrument on file. Used to
 * gate UI affordances (Day One, post-appointment search tree) that don't
 * apply while the job is still in pre-appointment / pitching state.
 */
export const APPOINTED_JOB_STATUSES: ReadonlyArray<JobStatus> = [
  "day_one",
  "active",
  "on_hold",
  "completed",
];

/**
 * Terminal statuses — no further state transitions are expected. `archived`
 * is also terminal but can be unarchived; `did_not_proceed` and `completed`
 * are end states.
 */
export const TERMINAL_JOB_STATUSES: ReadonlyArray<JobStatus> = [
  "did_not_proceed",
  "completed",
];

/**
 * Statuses that count as "in-flight" work — the firm is actively engaged
 * (pitching, day one, or business as usual) rather than paused or done.
 * Default job-status filter for the workspace calendar and task views so
 * completed / archived / on-hold matters don't drown out live work.
 */
export const ACTIVE_JOB_STATUSES: ReadonlyArray<JobStatus> = [
  "pre_appointment",
  "day_one",
  "active",
];

/**
 * State of a winding-up application filed BEFORE the current appointment
 * (MOR-901). Drives the ILRA s 91 relation-back day: an application that was
 * on foot when the administrators were appointed anchors the relation-back
 * day to its filing date, whereas one that had already been dismissed or
 * withdrawn cannot, so the day falls back to the date of appointment.
 *
 * The Act draws no distinction between dismissal and withdrawal for this
 * purpose (s 513B(b) covers both) — they are recorded separately only so the
 * practitioner's file reflects what actually happened.
 */
export const windingUpApplicationStatusValidator = v.union(
  /** No winding-up application preceded the appointment. */
  v.literal("none"),
  /** Filed and still on foot when the appointment was made. */
  v.literal("on_foot"),
  v.literal("dismissed"),
  v.literal("withdrawn"),
);

export type WindingUpApplicationStatus = Infer<
  typeof windingUpApplicationStatusValidator
>;

/**
 * How the bankruptcy was initiated (MOR-1252). Selects the s 115 pathway
 * that computes commencement of bankruptcy — see
 * `convex/keyDates/bankruptcyCommencement.ts`.
 */
export const bankruptcyPetitionTypeValidator = v.union(
  /** Sequestration order on a creditor's petition (s 43). */
  v.literal("creditors_petition"),
  /** Debtor's petition accepted by the Official Receiver (s 55). */
  v.literal("debtors_petition"),
  /**
   * Sequestration order made under Div 6 of Part IV (a failed composition)
   * or under Part X (a failed personal insolvency agreement) — s 115(1B).
   */
  v.literal("sequestration_order_other"),
);

export type BankruptcyPetitionType = Infer<
  typeof bankruptcyPetitionTypeValidator
>;

/**
 * The s 40(1) paragraph of the act of bankruptcy relied on (MOR-1252).
 * Paragraphs (j), (l), (m) and (n) take a DEEMED commission date under
 * s 40(4) to (7); the calculator surfaces that so the practitioner enters
 * the deemed date rather than the underlying event.
 */
export const actOfBankruptcyParagraphValidator = v.union(
  v.literal("a"),
  v.literal("b"),
  v.literal("c"),
  v.literal("d"),
  v.literal("da"),
  v.literal("daa"),
  v.literal("e"),
  v.literal("f"),
  v.literal("g"),
  v.literal("h"),
  v.literal("ha"),
  v.literal("hb"),
  v.literal("hc"),
  v.literal("hd"),
  v.literal("i"),
  v.literal("j"),
  v.literal("k"),
  v.literal("l"),
  v.literal("m"),
  v.literal("n"),
  v.literal("o"),
);

export type ActOfBankruptcyParagraph = Infer<
  typeof actOfBankruptcyParagraphValidator
>;

export const INVESTIGATION_PHASES = {
  PLAN: "plan",
  GATHER: "gather",
  REVIEW: "review",
  FINDINGS: "findings",
  REPORTING: "reporting",
  RECOVERIES: "recoveries",
} as const;

export const investigationPhaseValidator = v.union(
  v.literal("plan"),
  v.literal("gather"),
  v.literal("review"),
  v.literal("findings"),
  v.literal("reporting"),
  v.literal("recoveries"),
);

export type InvestigationPhase = Infer<typeof investigationPhaseValidator>;

export const INVESTIGATION_SCALES = {
  ESSENTIAL: "essential",
  MID: "mid",
  COMPLEX: "complex",
} as const;

export const investigationScaleValidator = v.union(
  v.literal("essential"),
  v.literal("mid"),
  v.literal("complex"),
);

export type InvestigationScale = Infer<typeof investigationScaleValidator>;

export const JOB_ROLES = {
  OWNER: "owner",
  MEMBER: "member",
  VIEWER: "viewer",
} as const;

export const jobRoleValidator = v.union(
  v.literal("owner"),
  v.literal("member"),
  v.literal("viewer"),
);

export type JobRole = Infer<typeof jobRoleValidator>;

export const companyNameVariantTypeValidator = v.union(
  v.literal("trading_name"),
  v.literal("trust_name"),
  v.literal("former_trading_name"),
);

export type CompanyNameVariantType = Infer<
  typeof companyNameVariantTypeValidator
>;

export const companyNameVariantSourceValidator = v.union(
  v.literal("abr"),
  v.literal("manual"),
);

export type CompanyNameVariantSource = Infer<
  typeof companyNameVariantSourceValidator
>;

/**
 * Additional company name lines for documents, separate from the registered
 * `entityName` so the legal identity remains stable.
 */
export const companyNameVariantValidator = v.object({
  name: v.string(),
  type: companyNameVariantTypeValidator,
  includeInDocuments: v.boolean(),
  source: companyNameVariantSourceValidator,
});

export type CompanyNameVariant = Infer<typeof companyNameVariantValidator>;

export const additionalPlaceOfBusinessSourceValidator = v.union(
  v.literal("manual"),
  v.literal("asic"),
);

export type AdditionalPlaceOfBusinessSource = Infer<
  typeof additionalPlaceOfBusinessSourceValidator
>;

/**
 * MOR-989 — a known trading address that is not (or not correctly) recorded
 * on the ASIC extract as the registered office or principal place of
 * business. Entered manually so it can flow into recommended searches (land
 * title), day-one documents, the conflict check, and reports. Structured
 * the same way as the principal place of business so downstream consumers
 * can treat the two identically.
 */
export const additionalPlaceOfBusinessValidator = v.object({
  street: v.string(),
  suburb: v.string(),
  state: v.string(),
  postcode: v.string(),
  includeInDocuments: v.boolean(),
  source: additionalPlaceOfBusinessSourceValidator,
});

export type AdditionalPlaceOfBusiness = Infer<
  typeof additionalPlaceOfBusinessValidator
>;

/**
 * MOR-544 — shared per-job asset view preferences. Drives which
 * valuation-source columns are shown on the Asset table (everyone on the
 * job sees the same columns) and which asset categories are visible on
 * the Overview's embedded asset list. Category toggles are display-only —
 * they never affect waterfall or table totals.
 */
export const assetViewPrefsValidator = v.object({
  /** Valuation sourceTypes shown as extra Asset table columns. */
  visibleValuationColumns: v.optional(v.array(v.string())),
  /** Group keys hidden on the Overview (assetType, or "secured"/"unsecured"). */
  overviewHiddenGroups: v.optional(v.array(v.string())),
  /** Master toggle for the Overview asset list (undefined ⇒ shown). */
  overviewShowAssets: v.optional(v.boolean()),
  /**
   * Column ids shown on the Overview's Estimated Outcome waterfall, in toggle
   * order. Columns are either a recovery scenario ("rocap"/"practitioner"/
   * "low"/"high") or a valuation source ("src:<sourceType>", e.g.
   * "src:balance_sheet") mirroring the Asset table's columns. Display-only —
   * hiding/showing a column never affects any figure. Undefined ⇒ the four
   * recovery scenarios are shown and valuation-source columns are hidden.
   */
  visibleWaterfallColumns: v.optional(v.array(v.string())),
});

/**
 * Jobs (insolvency/restructuring appointments).
 * Belongs to an organisation. Users access via jobMemberships.
 */
export const jobsTable = defineTable({
  orgId: v.id("organizations"),
  jobName: v.optional(v.string()),
  entityName: v.string(),
  /**
   * Human-readable per-firm job code (e.g. "J-0042"). Minted at creation
   * from `jobCodeSettings` and sent to InfoTrack as `clientReference` for
   * Xero cost reconciliation. Editable in job settings. Distinct from
   * `_id`; never used as a key. Undefined on jobs created before this
   * feature (they use the `morgan-job-<_id>` fallback).
   */
  jobCode: v.optional(v.string()),
  acn: v.optional(v.string()),
  abn: v.optional(v.string()),
  /**
   * MOR-340 trust handling. When the appointed company acts as trustee of
   * a trust, the trust is a distinct ABR entity with its own ABN.
   *
   * - `trustAbn`: ABN registered against the trust itself.
   * - `trustName`: Entity name of the trust per ABR lookup.
   * - `trusteeCapacity`:
   *   - `sole_trustee` — company has no ABN of its own; only operated as
   *     trustee. Low risk it traded in its personal capacity.
   *   - `dual_capacity` — company has its own ABN AND is trustee. Risk it
   *     traded in both capacities; investigation must consider both.
   *   - `unknown` — trust ABN captured but capacity not yet confirmed.
   */
  trustAbn: v.optional(v.string()),
  trustName: v.optional(v.string()),
  trusteeCapacity: v.optional(
    v.union(
      v.literal("sole_trustee"),
      v.literal("dual_capacity"),
      v.literal("unknown"),
    ),
  ),
  registeredAddress: v.optional(v.string()),
  /** Structured registered office address components */
  registeredOfficeStreet: v.optional(v.string()),
  registeredOfficeSuburb: v.optional(v.string()),
  registeredOfficeState: v.optional(v.string()),
  registeredOfficePostcode: v.optional(v.string()),
  /** Structured principal place of business address components */
  principalPlaceStreet: v.optional(v.string()),
  principalPlaceSuburb: v.optional(v.string()),
  principalPlaceState: v.optional(v.string()),
  principalPlacePostcode: v.optional(v.string()),
  /**
   * MOR-989 — further trading addresses the practitioner knows about that
   * the company search did not record (or recorded wrongly). Bounded: a
   * company has a handful of premises, not thousands.
   */
  additionalPlacesOfBusiness: v.optional(
    v.array(additionalPlaceOfBusinessValidator),
  ),
  companyNameVariants: v.optional(v.array(companyNameVariantValidator)),
  anzsicCode: v.optional(v.string()),
  /**
   * Optional for pre-appointment jobs (Flow A) — captured at intake but
   * not yet binding. Required (and validated by mutations) for any
   * status in {@link APPOINTED_JOB_STATUSES}.
   */
  appointmentType: v.optional(appointmentTypeValidator),
  /**
   * Optional for pre-appointment jobs — added at the conversion step
   * (A9) once the instrument of appointment is uploaded.
   */
  appointmentDate: v.optional(v.string()),
  /**
   * @deprecated Unified into `administrationStartDate` (MOR-813) — one
   * field for "the day the administration began", read by both the FEG
   * service calculation and the relation-back derivation. No longer
   * written; read as a fallback for rows created before the unification.
   */
  administrationDate: v.optional(v.string()),
  /**
   * FEG case-level dates (with `administrationStartDate` below). The Fair
   * Entitlements Guarantee measures an employee's service to the
   * insolvency practitioner appointment date, which is the administration
   * start date where the administration ran continuously into the
   * liquidation and the liquidation date otherwise. Captured separately
   * from `appointmentDate` because a job can have both, and only the
   * employee entitlements register needs to distinguish them.
   *
   * Resolved by `resolveFegInsolvencyDate` in
   * `convex/lib/employeeEntitlements.ts`.
   */
  liquidationDate: v.optional(v.string()),
  administrationContinuous: v.optional(v.boolean()),
  /**
   * Appointment history (MOR-813): the appointment type + date as they
   * stood immediately before each change of appointment type, most recent
   * last. Written by `recordAppointmentChange` (and by `updateJob` when NOA
   * extraction supplies a different type) so a VA→liquidation conversion
   * never loses the original administration's dates.
   *
   * Read by `resolveAdministrationStartDate` in
   * `convex/jobs/appointmentSwitch.ts` to find the ORIGINAL administration
   * through a deed — a VA→DOCA→CVL chain has no administration date on the
   * outgoing appointment, only in here — and by `mapJobToCanonicalFields`
   * to expose the prior appointment to day-one precedents.
   *
   * Deliberately stores the appointment IDENTITY only (type + date), not a
   * copy of the day-one fields. Court/bankruptcy scalars describe whichever
   * appointment is current and stay valid on the job row, so snapshotting
   * them per stage would create a second source of truth that silently
   * drifts.
   */
  priorAppointments: v.optional(
    v.array(
      v.object({
        appointmentType: appointmentTypeValidator,
        appointmentDate: v.optional(v.string()),
        changedAt: v.number(),
        /**
         * Who recorded the change. Optional: entries written before
         * MOR-901, and those from NOA extraction, have no explicit actor.
         */
        changedByUserId: v.optional(v.id("users")),
        /** Practitioner note explaining the change of appointment. */
        note: v.optional(v.string()),
      }),
    ),
  ),
  /**
   * State/territory of the appointee (practitioner), used for
   * business-day due-date offsets (MOR-836). Deliberately NOT the
   * company's location — s9 business days for the appointee's work run
   * on the appointee's calendar (confirmed by Paige). Free text parsed
   * via `parseJurisdiction`; falls back to the org default in
   * `orgTaskSettings.defaultAppointeeJurisdiction`.
   */
  appointeeJurisdiction: v.optional(v.string()),
  /**
   * Per-job override for auto-generated statutory calendar tasks
   * (MOR-839). "off" hides the derived statutory lodgement tasks for
   * this job, "on" shows them even when the org default disables them,
   * "inherit"/unset follows `orgTaskSettings.disableStatutoryTasks`.
   */
  statutoryTasksMode: v.optional(
    v.union(v.literal("inherit"), v.literal("on"), v.literal("off")),
  ),
  /**
   * @deprecated No longer written. The relation-back day is now derived
   * per appointment type by `deriveRelationBack` in
   * `convex/keyDates/derivations.ts` (it is NOT `appointmentDate − 6 months`
   * — that offset is the s588FA window, not the RBD). Kept optional for
   * backwards compatibility with any existing rows; safe to drop from the
   * schema once those rows have been cleared.
   */
  relationBackDate: v.optional(v.string()),
  /**
   * @deprecated No longer written. The investigation-scale concept has
   * been removed. Kept optional for backwards compatibility; safe to
   * drop this field and the `investigationScaleValidator` once existing
   * rows have been cleared.
   */
  investigationScale: v.optional(investigationScaleValidator),
  status: jobStatusValidator,
  /**
   * Set when the job's status transitions from `pre_appointment` to any
   * appointed status. Acts as a marker that the row started life as a
   * pre-appointment pitch and has since been converted. Surfaced in the
   * change log + UI to provide an audit trail without blowing up the
   * status enum.
   */
  appointmentRecordedAt: v.optional(v.number()),
  /**
   * Reason captured when a pre-appointment job is closed via
   * `markJobDidNotProceed` (status flips to `did_not_proceed`).
   * Free text for v1; consider promoting to a structured enum once
   * we have reporting needs.
   */
  lostReason: v.optional(v.string()),
  /**
   * The status the job held immediately before the most recent
   * transition. Lets us offer a one-click undo on conversion mistakes
   * without consulting `jobChangeLog`. Cleared on the next valid
   * transition once an admin confirms the new state.
   */
  previousStatus: v.optional(jobStatusValidator),
  /**
   * Firm-defined pipeline stage while the job is `pre_appointment`
   * (MOR-882). Points at an org-scoped `preAppointmentStatuses` row
   * (e.g. Lead → Pitched → Proposal). Kept after conversion / loss so
   * the pipeline can show where a closed pitch ended up.
   */
  preAppointmentStatusId: v.optional(v.id("preAppointmentStatuses")),
  /** Firm-defined pipeline labels applied to this job (MOR-882). */
  preAppointmentLabelIds: v.optional(v.array(v.id("preAppointmentLabels"))),
  /**
   * MOR-948 — Values for the firm's custom pipeline fields
   * (`preAppointmentFields`), keyed by field id. Stored as strings and
   * interpreted by each field's type; absent keys mean "not set".
   */
  preAppointmentCustomFields: v.optional(
    v.record(v.id("preAppointmentFields"), v.string()),
  ),
  /**
   * Fractional-index rank for manual ordering on the pre-appointment
   * kanban board (mirrors `jobKeyDates.boardRank`).
   */
  preAppointmentBoardRank: v.optional(v.string()),
  /**
   * The firm contact (category `referrer`) who referred this job to the
   * firm (MOR-882). Drives per-referrer conversion stats on the firm
   * contact detail panel and the referrer column/grouping in the
   * pre-appointment pipeline.
   */
  referrerFirmContactId: v.optional(v.id("firmContacts")),
  /**
   * DIRRI drafting state (MOR-882). The draft is authored in a universal-
   * agent chat thread with the declaration in the canvas: `dirriThreadId`
   * points at that thread so "Continue drafting" reopens the same
   * conversation, and the practitioner marks the declaration approved once
   * they've saved the final version to the job's files.
   */
  dirriThreadId: v.optional(v.string()),
  dirriStatus: v.optional(
    v.union(v.literal("drafting"), v.literal("approved")),
  ),
  dirriApprovedAt: v.optional(v.number()),
  dirriApprovedBy: v.optional(v.id("users")),
  currentPhase: investigationPhaseValidator,
  practitioners: v.array(v.string()),
  appointees: v.optional(v.array(v.string())),
  notes: v.optional(v.string()),
  createdBy: v.id("users"),
  /** Whether day-one precedent documents have been generated for this job */
  hasDayOnePrecedents: v.optional(v.boolean()),
  /**
   * Pointer to the documents row holding the uploaded Notice of Appointment.
   * Set when the file is uploaded during job creation (or replaced via the
   * Notice of Appointment settings tab). Used as a fast lookup for the
   * settings tab and as a downstream attachment source for precedents.
   */
  noticeOfAppointmentDocumentId: v.optional(v.id("documents")),
  /**
   * Custom tag selections for this job, keyed by tag group slug.
   * Used for auto-selecting templates during document generation.
   * e.g. { "company_type": ["pty_ltd"], "industry": ["construction"] }
   */
  customTagSelections: v.optional(v.record(v.string(), v.array(v.string()))),
  /**
   * Variant option selections for this job, keyed by variant group slug.
   * Drives sentence-level variant blocks in precedent templates so that a
   * single template can render different wording per job (e.g.
   * single vs. multiple practitioners).
   * e.g. { "practitioner_count": "multiple" }
   */
  variantSelections: v.optional(v.record(v.string(), v.string())),
  /**
   * Manually entered estimate of the practitioner's total fees for the
   * appointment, in AUD. Surfaced as the first deduction on the
   * Estimated Outcome Waterfall (job overview) so practitioners can
   * model dividend prospects before the formal s556 statement is
   * prepared. Currently the only manually-edited number on the
   * waterfall — every other class is sourced from agent-extracted
   * canonical entries.
   */
  estimatedPractitionerFees: v.optional(v.number()),
  /** MOR-544 — shared asset view prefs (see assetViewPrefsValidator). */
  assetViewPrefs: v.optional(assetViewPrefsValidator),
  /**
   * MOR-429 day-one fields — court orders, ASIC 505 notices, AFSA
   * certificates. All optional and per-case: populated automatically by
   * NOA extraction when the appointment type has a court/regulatory
   * instrument, and editable on the job overview otherwise. Drive
   * canonical fields in the `court` category for precedent generation.
   *
   * Plaintiff/defendant/bankrupt names are stored case-preserved —
   * statutory citation rules require they appear exactly as printed on
   * the order, so do NOT title-case before persisting.
   */
  courtName: v.optional(v.string()),
  courtState: v.optional(v.string()),
  courtNumber: v.optional(v.string()),
  /** Numeric portion of courtNumber, e.g. "425433" from "2025/00425433". */
  sequestrationNumber: v.optional(v.string()),
  proceedingMatterNumber: v.optional(v.string()),
  proceedingYear: v.optional(v.string()),
  courtOrderDate: v.optional(v.string()),
  /**
   * ISO date the application/originating process for winding up was *filed*
   * (Court Liquidation). This is earlier than `courtOrderDate` (the date the
   * court actually ordered the winding up) and is the relation-back day for
   * court-ordered liquidations. Manual entry today; ASIC published-notices
   * auto-extraction is a deferred follow-up.
   */
  windingUpApplicationDate: v.optional(v.string()),
  /**
   * ISO date a person first became administrator of the company under
   * Part 5.3A — the "s513C day" (MOR-813). Set only where the current
   * appointment followed a voluntary administration: it is then the
   * relation-back day for a CVL / simplified liquidation (s91, via the
   * s446A route) and for a court winding up ordered while the company was
   * under administration or a DOCA. Where the appointment followed a DOCA
   * that has since failed, this is the ORIGINAL administration's start,
   * not the deed's execution or termination date.
   *
   * Also the administration date the FEG service calculation measures to
   * when `administrationContinuous` is set — one field for "the day the
   * administration began", shared by both consumers (supersedes the
   * deprecated `administrationDate`). Auto-carried from the prior
   * appointment date when a VA job is switched to a liquidation type.
   */
  administrationStartDate: v.optional(v.string()),
  /**
   * State of any winding-up application filed BEFORE the appointment
   * (MOR-901), captured from the Prior Court Application block in Job
   * Settings → Appointment Details.
   *
   * Only asked on a voluntary administration — an administration is the
   * only appointment that can be preceded by a live winding-up
   * application. It is still READ after the VA converts to a liquidation,
   * because the answer keeps governing the relation-back day once the
   * winding up actually begins (see `resolvePriorApplication` in
   * `convex/keyDates/derivations.ts`).
   *
   * Unset behaves as `none` for the date, but the two are distinct in the
   * UI: unset means "not yet asked", `none` means "asked, and there was
   * no prior application".
   */
  windingUpApplicationStatus: v.optional(windingUpApplicationStatusValidator),
  /**
   * Practitioner override for the relation-back day (MOR-901), ISO date.
   *
   * Honoured by `deriveRelationBack` itself, so the calendar anchor, the
   * voidable-transaction review windows and the AI agents all read the same
   * date. This is the escape hatch for pathways the derivation doesn't
   * cover; unset means the date is derived.
   */
  relationBackDateOverride: v.optional(v.string()),
  /** First plaintiff (Court Liq) or applicant creditor (BKY Cred Petit). */
  plaintiffName: v.optional(v.string()),
  /** First defendant on a Court Liq. */
  defendantName: v.optional(v.string()),
  /** Respondent / debtor name on a BKY. */
  bankruptName: v.optional(v.string()),
  /**
   * MOR-1847 — the bankrupt's / debtor's date of birth, ISO date only
   * (YYYY-MM-DD, no time so it never shifts by timezone). Identifies the
   * individual on personal insolvency conflict checks.
   */
  bankruptDateOfBirth: v.optional(v.string()),
  /** AFSA administration number, e.g. "NSW 1262 of 2025/3". */
  administrationNumber: v.optional(v.string()),
  /** Currency string preserved verbatim (e.g. "$8,918.86"). */
  petitioningCreditorCosts: v.optional(v.string()),
  /** ISO date — drives BKY relation-back day computation. */
  actOfBankruptcyDate: v.optional(v.string()),
  /**
   * MOR-1252 — bankruptcy case record. Together with `actOfBankruptcyDate`
   * these feed `deriveBankruptcyCommencement` (s 115). All optional: unset
   * means "not yet captured", and the calculator blocks rather than
   * defaulting when a pathway cannot be determined.
   */
  bankruptcyPetitionType: v.optional(bankruptcyPetitionTypeValidator),
  /**
   * ISO date the petition was presented. On a `sequestration_order_other`
   * job this is the date the application for the order was made
   * (s 115(1B)).
   */
  petitionPresentedDate: v.optional(v.string()),
  /**
   * ISO date of the bankruptcy — the sequestration order, or acceptance of
   * the debtor's petition. Falls back to `courtOrderDate` then
   * `appointmentDate` when unset.
   */
  dateOfBankruptcy: v.optional(v.string()),
  /** s 40(1) paragraph of the act of bankruptcy relied on. */
  actOfBankruptcyParagraph: v.optional(actOfBankruptcyParagraphValidator),
  /**
   * Short description of the act relied on, e.g. "Failed to comply with
   * bankruptcy notice served 3 Feb 2025".
   */
  actOfBankruptcyDescription: v.optional(v.string()),
  /**
   * Debtor's petition only: was a creditor's petition pending when the
   * debtor's petition was presented (s 115(2) row 2)? Unset means the
   * question has not been answered, which blocks commencement.
   */
  creditorsPetitionPending: v.optional(v.boolean()),
  /** ISO commencement time the Court specified under s 115(2) row 1. */
  courtSpecifiedCommencementDate: v.optional(v.string()),
  /**
   * MOR-1255 — inputs for the individual insolvency date and the business
   * guard on the Books & Records presumptions (s 120(3A), s 121(4A)).
   */
  /** Whether the bankrupt carried on a business. Unanswered = undefined. */
  bankruptCarriedOnBusiness: v.optional(v.boolean()),
  /** ISO date of the earliest final judgment or order left unpaid. */
  earliestUnpaidJudgmentDate: v.optional(v.string()),
  /** ISO date from which the statement of affairs says debts could not be paid as they fell due. */
  statementOfAffairsInsolvencyDate: v.optional(v.string()),
  /**
   * s 115(1A): the s 41(7) bankruptcy-notice extension was found frivolous,
   * vexatious or without substantial merit, so commencement is the time
   * that would have applied without it.
   */
  bankruptcyNoticeExtensionDisregarded: v.optional(v.boolean()),
  /**
   * MOR-770 — estate/administration bank account opened for this job.
   * Entered manually in Job Settings → Job Details; drives the
   * `job_bank_*` canonical fields for precedent generation (e.g. letters
   * telling debtors/banks where to remit funds).
   */
  bankName: v.optional(v.string()),
  bankAccountName: v.optional(v.string()),
  bankBsb: v.optional(v.string()),
  bankAccountNumber: v.optional(v.string()),
  /**
   * MOR-485 — AI-generated narrative background ("mini wiki") shown at the
   * top of the job overview. Two paragraphs (≤400 words) summarising what
   * the business does, when it started, where it operated, and the nature
   * of its operations. Synthesised by the `sum_job_overview` Managed Agent
   * from extracted documents, firm-entered job fields, and Claude web
   * search. Editable by the user via TipTap; the agent respects manual
   * edits when regenerating.
   */
  overviewSummaryHtml: v.optional(v.string()),
  overviewSummaryUpdatedAt: v.optional(v.number()),
  overviewSummaryAgentUpdatedAt: v.optional(v.number()),
  overviewSummaryUserEditedAt: v.optional(v.number()),
  overviewSummarySources: v.optional(
    v.array(
      v.object({
        kind: v.union(
          v.literal("web"),
          v.literal("document"),
          v.literal("job_field"),
        ),
        label: v.string(),
        url: v.optional(v.string()),
        documentId: v.optional(v.id("documents")),
      }),
    ),
  ),
})
  .index("by_org_id", ["orgId"])
  .index("by_org_id_and_status", ["orgId", "status"])
  .index("by_org_id_and_appointment_type", ["orgId", "appointmentType"])
  .index("by_org_id_and_referrer", ["orgId", "referrerFirmContactId"]);

export type JobFields = Infer<typeof jobsTable.validator>;
